/**
 * Purpose: Shared plain-text ingredient/product safety status (Phase 2 FR-05).
 *          Text-only — no icons; null status renders "not yet assessed".
 * Inputs: safetyStatus, optional safetyNote, surface + ids for PostHog, optional
 *         element id suffix for unique DOM ids.
 * Outputs: Accessible text status (+ note); emits ingredient_safety_text_shown on mount.
 */
'use client'

import { useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { captureIngredientSafetyTextShown } from '@lib/interaction-telemetry'
import type { IngredientSafetyStatus, IngredientSafetySurface } from '@lib/models/types'

export type IngredientSafetyTextProps = {
  safetyStatus: IngredientSafetyStatus | null
  safetyNote?: string | null
  surface: IngredientSafetySurface
  ingredientId?: string
  productId?: string
  /** Suffix for DOM ids so multiple instances on one page stay unique. */
  idSuffix: string
}

export function IngredientSafetyText({
  safetyStatus,
  safetyNote,
  surface,
  ingredientId,
  productId,
  idSuffix,
}: IngredientSafetyTextProps) {
  const t = useTranslations('ingredientSafety')

  // Fire once per mount — properties are ids/status only (no PII).
  useEffect(() => {
    captureIngredientSafetyTextShown({
      ingredientId,
      productId,
      safetyStatus,
      surface,
    })
  }, [ingredientId, productId, safetyStatus, surface])

  const statusLabel =
    safetyStatus === null ? t('notYetAssessed') : t(`status.${safetyStatus}`)

  return (
    <div
      id={`ingredient-safety-text-${idSuffix}`}
      className="ingredient-safety-text text-sm text-black/80"
      role="status"
      aria-live="polite"
    >
      <p id={`ingredient-safety-status-${idSuffix}`} className="m-0">
        <span id={`ingredient-safety-label-${idSuffix}`} className="text-black/50">
          {t('label')}
          {': '}
        </span>
        {/* Status is plain text — never color-only or icon-only (WCAG 2.1 AA). */}
        <span id={`ingredient-safety-value-${idSuffix}`}>{statusLabel}</span>
      </p>
      {safetyNote ? (
        <p id={`ingredient-safety-note-${idSuffix}`} className="m-0 mt-1 text-black/60">
          {safetyNote}
        </p>
      ) : null}
    </div>
  )
}
